import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { configuration } from '../../../configuration';
import {
  isOAuthRedirectResponse,
  parkDestination,
  parseDestination,
  recordOAuthFailure,
  reportOAuthExchangeFailure,
  reportSessionProbeOutcome,
} from './postAuthRedirect';

let configured = false;
let hubListenerRegistered = false;

// `?sr_staff=1` on the URL pins this browser to the Google-only msr3-staff
// client via localStorage so staff stay on it across every MSR3 route.
// Cleared on sign-out (see CognitoManagedLoginProvider).
const ACTIVE_CLIENT_STORAGE_KEY = 'sr-active-cognito-client';
const STAFF_CLIENT_VALUE = 'staff';
const STAFF_URL_PARAM = 'sr_staff';

function readActiveClient(): null | string {
  try {
    return window.localStorage.getItem(ACTIVE_CLIENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeActiveClient(value: string): void {
  try {
    window.localStorage.setItem(ACTIVE_CLIENT_STORAGE_KEY, value);
  } catch {
    // best-effort
  }
}

export function clearActiveCognitoClient(): void {
  try {
    window.localStorage.removeItem(ACTIVE_CLIENT_STORAGE_KEY);
  } catch {
    // best-effort
  }
}

export function isStaffMode(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);

  if (params.get(STAFF_URL_PARAM) === '1') {
    writeActiveClient(STAFF_CLIENT_VALUE);

    return true;
  }

  return readActiveClient() === STAFF_CLIENT_VALUE;
}

function resolveActiveClientId(): string {
  if (isStaffMode() && configuration.COGNITO_APP_CLIENT_ID_MSR3_STAFF) {
    return configuration.COGNITO_APP_CLIENT_ID_MSR3_STAFF;
  }

  return configuration.COGNITO_APP_CLIENT_ID;
}

/**
 * Recover the post-auth destination from the OAuth `state` round-trip
 * (ADR-CM-037).
 *
 * Registered here, at module scope, rather than inside `LoginCallback`: that
 * route is lazy, so it would usually mount after Amplify has already
 * dispatched and the destination would be gone. It must also run *before*
 * `Amplify.configure()` below, because `configure()` is what kicks off OAuth
 * completion -- a listener added afterwards races the token exchange and
 * would fail rarely rather than never.
 */
function registerOAuthHubListener(): void {
  if (hubListenerRegistered) return;
  hubListenerRegistered = true;

  Hub.listen('auth', ({ payload }) => {
    if (payload.event === 'customOAuthState') {
      // Untrusted: `customState` round-trips through the browser, so it gets
      // the same origin allowlist as the query parameter it came from.
      const destination = parseDestination(payload.data);

      if (destination) parkDestination(destination);

      return;
    }

    if (payload.event === 'signInWithRedirect_failure') {
      // Amplify raises this for an *abandoned* flow too -- a bare page load
      // with an in-flight OAuth record (e.g. browser Back out of the Hosted
      // UI) throws a cancellation before any exchange is attempted. Treating
      // that as a hard error would strand the member on an error page where
      // the previous behaviour, re-initiating SSO, is correct. Only a real
      // provider response is recorded.
      if (!isOAuthRedirectResponse()) return;
      const failure = {
        message: payload.data?.error?.message,
        name: payload.data?.error?.name,
      };

      recordOAuthFailure(failure);
      // The line above is for the member; this one is for us. Without it a
      // failed code exchange is visible only as an error screen, and how
      // often one happens stays exactly as unanswerable as it was in
      // APP-2364.
      reportOAuthExchangeFailure(failure, { origin: 'configureAmplify' });
    }
  });
}

export function configureAmplifyAuth(): void {
  if (configured || !configuration.COGNITO_ENABLED) return;
  configured = true;

  registerOAuthHubListener();

  const staffMode = isStaffMode();

  // `aws.cognito.signin.user.admin` is only valid when the user is
  // authenticated directly by Cognito (password grant). Federated
  // (Google-only) clients return `invalid_scope` when this is requested.
  const scopes = staffMode
    ? ['openid', 'email', 'profile']
    : ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'];

  Amplify.configure({
    Auth: {
      Cognito: {
        loginWith: {
          oauth: {
            domain: configuration.COGNITO_DOMAIN,
            providers: ['Google'],
            redirectSignIn: [window.location.origin + '/login-callback'],
            redirectSignOut: [window.location.origin + '/logged-out'],
            responseType: 'code',
            scopes,
          },
        },
        signUpVerificationMethod: 'code',
        userPoolClientId: resolveActiveClientId(),
        userPoolId: configuration.COGNITO_USER_POOL_ID,
      },
    },
  });
}

export const SESSION_PROBE_TIMEOUT_MS = 5_000;

/**
 * Read the stored session before `Authenticator.Provider` mounts: Amplify UI
 * signs out on any non-network refresh failure, dropping `return_to`. Read
 * first, a dead refresh token (expired, or revoked by MSR2's sign-out) is
 * cleared while nothing is listening. Never rejects; the timeout stops a hung
 * Cognito call blocking the app. Relies on Amplify's default localStorage
 * store: it clears the tokens unawaited, finishing before the Provider mounts.
 */
export function probeStoredSession(
  timeoutMs = SESSION_PROBE_TIMEOUT_MS,
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  // First to settle reports; a call finishing after the timeout stays quiet.
  let reported = false;
  const report: typeof reportSessionProbeOutcome = (outcome, extra) => {
    if (reported) return;
    reported = true;
    reportSessionProbeOutcome(outcome, extra);
  };
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      report('timeout', { timeoutMs });
      resolve();
    }, timeoutMs);
  });
  const probe = Promise.resolve()
    .then(() => fetchAuthSession())
    .then(
      () => report('read'),
      (err: unknown) =>
        report('error', {
          errorName: err instanceof Error ? err.name : String(err),
        }),
    );

  return Promise.race([probe, timeout]).finally(() => clearTimeout(timer));
}

let storedSessionProbe: Promise<void> | undefined;

/** The one boot-time probe, shared by every caller. */
export function waitForStoredSessionProbe(): Promise<void> {
  storedSessionProbe ??= configuration.COGNITO_ENABLED
    ? probeStoredSession()
    : Promise.resolve();

  return storedSessionProbe;
}

export async function getCognitoAccessToken(): Promise<string> {
  try {
    const session = await fetchAuthSession();

    return session.tokens?.accessToken?.toString() ?? '';
  } catch {
    return '';
  }
}

export type CognitoTokens = {
  accessToken: string;
  expiresAt: number;
  idToken?: string;
};

// Only used if Cognito ever issues an access token without `exp`. Anything
// falsy there would read as already-expired in the embed, which would re-ask in
// a tight loop rather than degrade.
const FALLBACK_TOKEN_TTL_MS = 5 * 60_000;

/**
 * Session for handing to an embedded MSR2 frame (see APP-2221).
 *
 * `fetchAuthSession` refreshes under the hood once the cached token is close to
 * expiry, so calling this again is what renews the embed's token -- the refresh
 * token itself never leaves MSR3.
 *
 * The id token is deliberately optional. Amplify types it as optional
 * (`AuthTokens.idToken?`) and its refresh path drops it whenever Cognito's
 * response omits `IdToken`, so it reflects the last refresh rather than the
 * session. The embed uses it only for the `X-User-Email` hint, so refusing to
 * hand over the access token without it would break a working embed roughly an
 * hour in, at the first refresh that came back without one.
 */
export async function getCognitoTokens(): Promise<CognitoTokens | null> {
  try {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken;

    if (!accessToken) {
      console.warn('No Cognito access token available for the MSR2 embed.');

      return null;
    }

    // `exp` is seconds since epoch; the embed schedules its refresh off this.
    const exp = accessToken.payload.exp;

    return {
      accessToken: accessToken.toString(),
      expiresAt: exp ? exp * 1000 : Date.now() + FALLBACK_TOKEN_TTL_MS,
      idToken: session.tokens?.idToken?.toString(),
    };
  } catch (err) {
    console.warn('Could not read the Cognito session for the MSR2 embed.', err);

    return null;
  }
}

configureAmplifyAuth();
